import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
} from "@mui/material";
import CommonTable from "../CommonTable";
import useItemCoverage from "../../hooks/useItemCoverage";
import DownloadIcon from "@mui/icons-material/Download";

const branchSelected = (branchIds) => {
  if (Array.isArray(branchIds)) return branchIds.some(Boolean);
  return !!branchIds;
};

export default function ItemDashboard({ filters, branches }) {
  const {
    loading,
    mapping,
    data,
    fetchStockMapping,
    fetchItemCoverage,
    exportItemCoverage,
  } = useItemCoverage();
  const [target, setTarget] = useState(""); //item/collection info

  // load mapping when cohort changes
  useEffect(() => {
    if (
      (filters.orgId || filters.organization) &&
      filters.academicYear &&
      filters.grade &&
      branchSelected(filters.branchIds)
    ) {
      fetchStockMapping({
        orgId: filters.orgId || filters.organization,
        academicYear: filters.academicYear,
        grade: filters.grade,
        program: filters.program || undefined,
        branchIds: filters.branchIds,
      });
      setTarget("");
    }
  }, [
    filters.orgId,
    filters.organization,
    filters.academicYear,
    filters.grade,
    filters.program,
    filters.branchIds,
    fetchStockMapping,
  ]);

  // run coverage whenever a target is picked
  useEffect(() => {
    if (!target || !target.id) return;
    fetchItemCoverage({
      academicYear: filters.academicYear,
      grade: filters.grade,
      program: filters.program || undefined,
      branchIds: filters.branchIds,
      targetType: target?.type,
      targetId: target?.id,
    });
  }, [target, filters, fetchItemCoverage]);

  const targetOptions = useMemo(() => {
    const itemList = (mapping.items || []).map((x) => ({
      id: x.item?._id,
      name: x.item?.name,
      qty: x.quantity,
      type: "item",
    }));
    const colList = (mapping.collections || []).map((x) => ({
      id: x.collection?._id,
      name: x.collection?.name,
      qty: x.quantity,
      type: "collection",
    }));
    return [...colList, ...itemList];
  }, [mapping]);

  const purchasedCols = useMemo(
    () => [
      {
        label: "Target Name",
        getExportValue: (s) => target.name,
        exportOnly: true,
      },
      {
        label: "Target Type",
        getExportValue: (s) => target.type,
        exportOnly: true,
      },
      { label: "Student ID", field: "studentId" },
      { label: "Name", field: "studentName", flex: true },
      {
        label: "Branch",
        getExportValue: (s) =>
          branches.filter((b) => b?._id == filters?.branchIds)[0]?.name,
        exportOnly: true,
      },
      {
        label: "Grade",
        getExportValue: (s) => filters.grade,
        exportOnly: true,
      },
      { label: "Purchased Qty", field: "purchasedQty" },
      { label: "Delivered Qty", field: "deliveredQty" },
      { label: "Item Price Total", field: "itemPrice" },
      {
        label: "Sale ID (Amount)",
        renderCell: (r) =>
          r.saleRefs?.length
            ? r.saleRefs
                .map(
                  (s) =>
                    s?.saleNumber?.replace(/^PURCHASE-/, "") +
                    " (" +
                    s.totalAmount +
                    ")"
                )
                .join(", ")
            : "-",
        getSearchValue: (r) => (r.saleNumbers ? r.saleNumbers.join(" ") : ""),
      },
    ],
    []
  );

  const notPurchasedCols = useMemo(
    () => [
      {
        label: "Target Name",
        getExportValue: (s) => target.name,
        exportOnly: true,
      },
      {
        label: "Target Type",
        getExportValue: (s) => target.type,
        exportOnly: true,
      },
      { label: "Student ID", field: "studentId" },
      { label: "Name", field: "studentName", flex: true },
      {
        label: "Branch",
        getExportValue: (s) =>
          branches.filter((b) => b?._id == filters?.branchIds)[0]?.name,
        exportOnly: true,
      },
      {
        label: "Grade",
        getExportValue: (s) => filters.grade,
        exportOnly: true,
      },
    ],
    []
  );

  return (
    <Box>
      {/* Export + Select */}
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() =>
            exportItemCoverage({
              academicYear: filters.academicYear,
              grade: filters.grade,
              program: filters.program || undefined,
              branchIds: filters.branchIds, // can be single or multiple
            })
          }
          disabled={
            loading ||
            !filters.academicYear ||
            !filters.grade ||
            !branchSelected(filters.branchIds)
          }
        >
          Download Branch Coverage
        </Button>

        <FormControl sx={{ minWidth: 320 }}>
          <InputLabel>Item/Collection (Books)</InputLabel>
          <Select
            label="Item/Collection (Books)"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={!targetOptions.length}
          >
            {targetOptions.map((o) => (
              <MenuItem key={o.id} value={o}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!loading &&
        filters.grade &&
        branchSelected(filters.branchIds) &&
        !targetOptions.length && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No book items or collections found for this branch and grade. This
            report uses Student Book Mapping — not uniforms. Add mappings under
            Store Sales → Student Book Mapping for the selected branch, grade,
            and program.
          </Typography>
        )}

      {loading && <CircularProgress />}

      {data && (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Total Students: {data.summary.totalStudents} &nbsp;|&nbsp;
            Purchased: {data.summary.purchasedCount} &nbsp;|&nbsp; Yet to
            Purchase: {data.summary.notPurchasedCount}
          </Typography>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Purchased
          </Typography>
          <CommonTable
            downloadFileName="purchased_students"
            exportable
            columns={purchasedCols}
            rows={data.purchased}
          />

          <Typography variant="h6" sx={{ mt: 4 }}>
            Students Yet to Purchase
          </Typography>
          <CommonTable
            downloadFileName="not_purchased_students"
            exportable
            columns={notPurchasedCols}
            rows={data.notPurchased}
          />
        </>
      )}
    </Box>
  );
}
